import type { RefObject } from "react";
import {
  CheckCircle2,
  ImageOff,
  RotateCcw
} from "lucide-react";

import type { ActivityCompletionMetrics } from "@/app/integration/contracts";
import {
  AudioReplayButton,
  CurriculumVoiceButton,
  PrimaryCommandButton
} from "@/ui";

import type { HabitsAudioReplayRequest } from "./player-contracts";
import type { HabitsFeedbackModel } from "./types";

interface HabitsVoiceControl {
  readonly isPlaying: boolean;
  readonly label: string;
  readonly onPlay: () => void;
}

interface HabitsInstructionHeaderProps {
  readonly difficulty: number;
  readonly instruction: {
    readonly audioPath: string;
    readonly caption: string;
  };
  readonly instructionId: string;
  readonly instructionPlaying: boolean;
  readonly levelId: string;
  readonly onAudioReplay: (request: HabitsAudioReplayRequest) => void;
  readonly title: string;
  readonly titleId: string;
}

export function HabitsInstructionHeader({
  difficulty,
  instruction,
  instructionId,
  instructionPlaying,
  levelId,
  onAudioReplay,
  title,
  titleId
}: HabitsInstructionHeaderProps) {
  return (
    <>
      <header className="habits-player__header">
        <div className="habits-player__heading">
          <p className="habits-player__eyebrow">
            习惯岛 · 第 {difficulty} 级
          </p>
          <h2 id={titleId}>{title}</h2>
        </div>
        <AudioReplayButton
          isPlaying={instructionPlaying}
          label="再听题目"
          onClick={() =>
            onAudioReplay({
              audioPath: instruction.audioPath,
              caption: instruction.caption,
              kind: "instruction",
              levelId
            })
          }
          playingLabel="正在播放题目"
        />
      </header>
      <p className="habits-player__instruction" id={instructionId}>
        {instruction.caption}
      </p>
    </>
  );
}

interface HabitsSceneImageProps {
  readonly src: string;
}

export function HabitsSceneImage({ src }: HabitsSceneImageProps) {
  return (
    <div aria-hidden="true" className="habits-player__scene">
      <div className="habits-player__scene-fallback">
        <ImageOff />
      </div>
      <img
        alt=""
        className="habits-player__scene-image"
        onError={(event) => {
          event.currentTarget.hidden = true;
        }}
        src={src}
      />
    </div>
  );
}

interface HabitsFeedbackNoticeProps {
  readonly feedback: HabitsFeedbackModel | null;
  readonly isPlaying: boolean;
  readonly onReplay: () => void;
}

export function HabitsFeedbackNotice({
  feedback,
  isPlaying,
  onReplay
}: HabitsFeedbackNoticeProps) {
  if (feedback === null) {
    return <div className="habits-player__feedback-space" />;
  }

  return (
    <div className="habits-player__feedback-row">
    <p
      aria-live="polite"
      className="habits-player__feedback"
      data-feedback-kind={feedback.kind}
      role="status"
    >
      {feedback.text}
    </p>
      <CurriculumVoiceButton
        isPlaying={isPlaying}
        label={feedback.text}
        onPlay={onReplay}
      />
    </div>
  );
}

interface HabitsResultProps {
  readonly description: string;
  readonly descriptionVoice?: HabitsVoiceControl;
  readonly metrics: ActivityCompletionMetrics;
  readonly onReplay: () => void;
  readonly resultHeadingRef: RefObject<HTMLHeadingElement | null>;
}

function formatRate(rate: number): string {
  return Math.round(rate * 100) + "%";
}

export function HabitsResult({
  description,
  descriptionVoice,
  metrics,
  onReplay,
  resultHeadingRef
}: HabitsResultProps) {
  return (
    <section className="habits-player__result">
      <CheckCircle2
        aria-hidden="true"
        className="habits-player__result-icon"
      />
      <h3 ref={resultHeadingRef} tabIndex={-1}>
        这一关完成啦
      </h3>
      <div className="habits-player__result-description">
        <p role="status">{description}</p>
        {descriptionVoice === undefined ? null : (
          <CurriculumVoiceButton
            isPlaying={descriptionVoice.isPlaying}
            label={descriptionVoice.label}
            onPlay={descriptionVoice.onPlay}
          />
        )}
      </div>
      <dl className="habits-player__metrics">
        <div>
          <dt>选择正确率</dt>
          <dd>{formatRate(metrics.correctRate)}</dd>
        </div>
        <div>
          <dt>提示</dt>
          <dd>{metrics.hintCount} 次</dd>
        </div>
        <div>
          <dt>再试</dt>
          <dd>{metrics.retryCount} 次</dd>
        </div>
      </dl>
      <PrimaryCommandButton icon={RotateCcw} onClick={onReplay}>
        再玩一次
      </PrimaryCommandButton>
    </section>
  );
}
